import { Check as CheckIcon } from '@mui/icons-material'
import { alpha, Box, Button, Divider, Menu, MenuItem, Tooltip, Typography } from '@mui/material'
import React, { useCallback, useState } from 'react'
import { EntityAvatar } from '@/components/EntityAvatar/EntityAvatar'
import type { IDigitalFileSimplified } from '@/modules/DigitalFile/DigitalFile'
import type { IEntitySimplified } from '@/modules/Entity/Entity'

export interface INavbarContextItem {
  entity: IEntitySimplified
  logo?: IDigitalFileSimplified | null
  trailing?: React.ReactNode
}

interface IProps {
  icon: React.ReactNode
  label: string
  emptyLabel?: React.ReactNode
  menuHeader: React.ReactNode
  items: INavbarContextItem[]
  currentId?: number
  onSelect: (entity: IEntitySimplified) => void
  footer?: (close: () => void) => React.ReactNode
}

const NavbarContextSelectorInternal: React.FC<IProps> = ({
  icon,
  label,
  emptyLabel,
  menuHeader,
  items,
  currentId,
  onSelect,
  footer,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const handleOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }, [])

  const handleClose = useCallback(() => {
    setAnchorEl(null)
  }, [])

  return (
    <Box sx={{ flexShrink: 0 }}>
      <Tooltip title={label}>
        <Button
          type='button'
          variant='text'
          color='inherit'
          onClick={handleOpen}
          startIcon={icon}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            letterSpacing: 0.2,
            px: 1.5,
            py: 0.75,
            borderRadius: 1.5,
            bgcolor: (theme) => alpha(theme.palette.common.white, 0.08),
            transition: 'background-color 150ms ease',
            '&:hover': {
              bgcolor: (theme) => alpha(theme.palette.common.white, 0.18),
            },
          }}
        >
          {label || emptyLabel}
        </Button>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        keepMounted
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={!!anchorEl}
        onClose={handleClose}
        slotProps={{
          paper: {
            elevation: 8,
            sx: { mt: 1.25, minWidth: 280, maxHeight: 420, borderRadius: 2 },
          },
        }}
      >
        <Typography
          variant='caption'
          sx={{
            display: 'block',
            px: 2,
            pt: 1.5,
            pb: 0.5,
            color: 'text.secondary',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
          }}
        >
          {menuHeader}
        </Typography>
        <Divider sx={{ my: 0.5 }} />
        {items.map(({ entity, logo, trailing }) => {
          const isCurrent = currentId === entity.id

          return (
            <MenuItem
              key={entity.id}
              selected={isCurrent}
              onClick={() => {
                onSelect(entity)
                handleClose()
              }}
              sx={{ py: 1, gap: 1.5 }}
            >
              <EntityAvatar fullName={entity.fullName} logo={logo} size={28} />
              <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                <Typography noWrap sx={{ fontWeight: 600, lineHeight: 1.25 }}>
                  {entity.eapcID ?? entity.fullName}
                </Typography>
                <Typography
                  variant='caption'
                  noWrap
                  sx={{ display: 'block', color: 'text.secondary' }}
                >
                  {entity.fullName}
                </Typography>
              </Box>
              {trailing}
              {isCurrent && <CheckIcon fontSize='small' color='primary' />}
            </MenuItem>
          )
        })}
        {footer && <Divider sx={{ my: 0.5 }} />}
        {footer?.(handleClose)}
      </Menu>
    </Box>
  )
}

export const NavbarContextSelector = React.memo(
  NavbarContextSelectorInternal,
) as typeof NavbarContextSelectorInternal
